/**
 * Pure derivation of a version's human approval decision from its `reviews[]`.
 *
 * The Ask Puntt agent records a human's decision on a document version from the
 * chat (rembrandt `SetVersionReviewStatusTool`), writing a per-reviewer entry
 * onto the ticket's revision board. This module turns that array into the single
 * current decision the approval badge shows in the shared project review header.
 * No React / rendering here so it can be unit-tested (and reasoned about) on its
 * own.
 */

type ReviewerLike =
  | string
  | { _id?: string; name?: string | null }
  | null
  | undefined;

/** A single per-version review record, structurally (matches `TicketReview`). */
export type VersionReviewLike = {
  reviewer?: ReviewerLike;
  approval?: boolean | null;
  decision?: "approved" | "changes_requested" | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
};

export type VersionApproval = {
  decision: "approved" | "changes_requested";
  reviewerName?: string;
  at?: Date;
};

function reviewerName(reviewer: ReviewerLike): string | undefined {
  if (reviewer && typeof reviewer === "object") {
    return reviewer.name ?? undefined;
  }
  return undefined;
}

function reviewTime(review: VersionReviewLike): Date | undefined {
  const raw = review.updatedAt ?? review.createdAt;
  if (!raw) return undefined;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Normalize one review record to a decision, or null if it carries none. */
function reviewDecision(
  review: VersionReviewLike,
): "approved" | "changes_requested" | null {
  if (
    review.decision === "approved" ||
    review.decision === "changes_requested"
  ) {
    return review.decision;
  }
  // Legacy fallback: a boolean `approval === true` means approved. `false` is
  // ambiguous (never-reviewed vs. rejected) so we do NOT infer a decision.
  if (review.decision == null && review.approval === true) {
    return "approved";
  }
  return null;
}

/**
 * Pick the most recent decided review across a version's `reviews[]`.
 * Returns null when no human has approved or requested changes yet, so the
 * caller renders nothing. Falsifiable: drop the `decision` read and the
 * changes-requested case collapses to the legacy approved-only fallback.
 */
export function deriveVersionApproval(
  reviews: readonly VersionReviewLike[] | null | undefined,
): VersionApproval | null {
  let best: VersionApproval | null = null;
  let bestAt = -Infinity;
  for (const review of reviews ?? []) {
    const decision = reviewDecision(review);
    if (!decision) continue;
    const at = reviewTime(review);
    const rank = at ? at.getTime() : 0;
    if (best === null || rank >= bestAt) {
      best = { decision, reviewerName: reviewerName(review.reviewer), at };
      bestAt = rank;
    }
  }
  return best;
}

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** Human-readable "Approved by Dana on Jul 10, 2026" for tooltip + aria-label. */
export function describeApproval(approval: VersionApproval): string {
  const label =
    approval.decision === "approved" ? "Approved" : "Changes requested";
  const who = approval.reviewerName ? ` by ${approval.reviewerName}` : "";
  const when = approval.at ? ` on ${DATE_FMT.format(approval.at)}` : "";
  return `${label}${who}${when}`;
}
